import type { AuctisyncSupabase } from './client.js';

/**
 * Paying an invoice (Run Brief P2.9).
 *
 * WHY THIS LIVES HERE. This is a bidder's money. The website already owns the
 * wording for every payment failure and the call that starts a payment, and a
 * bidder told one thing on their phone and another on the site — about why
 * their card did not go through — has reason to distrust both. Jim's rule: no
 * mechanical divergence between the app and the website, wording included.
 *
 * WHAT THIS FILE DOES NOT DECIDE. Every money-safety contract lives in the
 * `create-invoice-payment-intent` Edge Function and is enforced server-side:
 * the payable gate (only `issued` — `partially_paid` is excluded because the EF
 * charges the FULL total and would overcharge a part-payer), the in-flight
 * pre-check that 409s when a payment already succeeded or is processing, the
 * stale-PI cancel sweep, the Stripe idempotency key, and the unique-index
 * backstop. This module carries a request there and renders what comes back.
 */

/** Payable by card ONLY while `issued`. */
export function canPayInvoiceByCard(status: string): boolean {
  // `partially_paid` is deliberately absent. The EF charges total_cents, and
  // `invoices` has no balance column, so charging a part-paid invoice would
  // take the full amount a second time (money-safety finding #3). A client that
  // offered the button anyway would just be asking the server to 409.
  return status === 'issued';
}

const MESSAGES: Record<string, string> = {
  stripe_charge_failed: 'Your card was declined. Please try a different card.',
  stripe_attach_pm_failed: 'We could not save this card. Please try a different card.',
  stripe_setup_intent_failed: 'We could not open the secure card form. Please try again.',
  payment_in_progress:
    'A payment for this invoice is already processing. This page will update automatically.',
  payment_already_succeeded:
    'This invoice looks paid already. This page will update automatically.',
  card_online_disabled: "This auction house doesn't accept online card payments.",
  invoice_not_payable:
    "This invoice isn't payable right now. Refresh the page for its latest status.",
  invoice_voided: 'This invoice has been voided and no longer needs payment.',
  tenant_not_connected: "This auction house isn't set up to accept cards yet.",
  tenant_charges_disabled: "This auction house isn't set up to accept cards yet.",
  not_registered_with_tenant:
    "You can use a card here once you're an approved bidder with this auction house.",
  pm_backs_active_hold:
    'This card is holding a deposit on an active auction. You can remove it once that auction settles.',
  not_your_card: "That card isn't on your account.",
};

/**
 * Friendly copy for a payment Edge Function error code.
 *
 * The payment EFs return short machine codes so the client owns the wording. A
 * bidder must never see a raw code: anything still shaped like a machine token
 * falls back to a generic message, while human-readable prose (a Stripe decline
 * message the EF passed through) is shown as-is.
 */
export function friendlyPaymentError(code: string | null | undefined): string {
  if (!code) return 'Something went wrong. Please try again.';
  const msg = MESSAGES[code];
  if (msg) return msg;
  // snake_case token we don't recognise → never surface it raw.
  if (/^[a-z0-9_]+$/.test(code)) return 'Something went wrong. Please try again.';
  // Otherwise it is already prose (Stripe message, network error) — show it.
  return code;
}

/**
 * Error codes that mean "a payment is already happening" rather than "this
 * failed". Both are 409s and NEITHER is the bidder's problem: the right
 * response is to stop offering the button and wait for the webhook, not to
 * show a red error and invite a second attempt.
 */
export function isPaymentAlreadyUnderway(code: string | null | undefined): boolean {
  return code === 'payment_in_progress' || code === 'payment_already_succeeded';
}

/** What the EF hands back when a payment can be started. */
export interface InvoicePaymentIntent {
  clientSecret: string;
  /** The tenant's Connect account. Stripe MUST be initialised against it. */
  stripeAccountId: string;
  /**
   * Stripe's own PI status. `requires_action` means the server reused a PI that
   * is stuck on a bank check — that one already has a card attached, so it is
   * finished with a confirmation call rather than by collecting a card again.
   */
  piStatus: string | null;
  amountCents: number | null;
}

export type StartInvoicePaymentResult =
  | { kind: 'ready'; intent: InvoicePaymentIntent }
  /** Paid between the screen loading and the button being pressed. */
  | { kind: 'already_paid' }
  /** A payment is in flight or already succeeded — wait, do not offer to retry. */
  | { kind: 'underway'; message: string }
  | { kind: 'error'; message: string };

interface EdgeFunctionResponse {
  client_secret?: string;
  stripe_account_id?: string;
  stripe_payment_intent_id?: string;
  amount_cents?: number;
  pi_status?: string;
  already_paid?: boolean;
  ok?: boolean;
  error?: string;
}

/**
 * Ask the server to create (or return) the PaymentIntent for an invoice.
 *
 * Never throws. Every outcome is one of the four kinds above, because a screen
 * that has to try/catch a payment will eventually get it wrong — and getting it
 * wrong here means either a double charge or a bidder stuck unable to pay.
 *
 * `saveCard` asks Stripe to keep the card for future off-session use. It is the
 * bidder's choice and defaults to false: silently storing someone's card is not
 * a default anyone consented to.
 */
export async function startInvoicePayment(
  supabase: AuctisyncSupabase,
  input: { invoiceId: string; saveCard?: boolean },
): Promise<StartInvoicePaymentResult> {
  let data: EdgeFunctionResponse | null = null;
  let code: string | null = null;

  try {
    const res = (await supabase.functions.invoke('create-invoice-payment-intent', {
      body: { invoice_id: input.invoiceId, save_card: input.saveCard ?? false },
    })) as unknown as {
      data: EdgeFunctionResponse | null;
      error: {
        message?: string;
        context?: { json?: () => Promise<{ error?: string }> };
      } | null;
    };

    if (res.error) {
      // The EF's machine code lives in the RESPONSE BODY, not in the thrown
      // error's message — a non-2xx from supabase-js surfaces as a generic
      // "Edge Function returned a non-2xx status code" unless the body is read.
      // Without this, every 409 would render as that sentence and the
      // in-flight case would look like a crash.
      const ctx = res.error.context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          code = (await ctx.json())?.error ?? null;
        } catch {
          code = null;
        }
      }
      code = code ?? res.error.message ?? 'Request failed.';
    } else {
      data = res.data;
      code = data?.error ?? null;
    }
  } catch (err) {
    code = err instanceof Error ? err.message : 'Request failed.';
  }

  if (code !== null) {
    return isPaymentAlreadyUnderway(code)
      ? { kind: 'underway', message: friendlyPaymentError(code) }
      : { kind: 'error', message: friendlyPaymentError(code) };
  }

  if (data?.already_paid) return { kind: 'already_paid' };

  if (!data?.client_secret || !data.stripe_account_id) {
    // Refusing to proceed on a partial response is deliberate: initialising
    // Stripe without the Connect account would charge the wrong account.
    return { kind: 'error', message: friendlyPaymentError(null) };
  }

  return {
    kind: 'ready',
    intent: {
      clientSecret: data.client_secret,
      stripeAccountId: data.stripe_account_id,
      piStatus: data.pi_status ?? null,
      amountCents: data.amount_cents ?? null,
    },
  };
}
